import { ChangeDetectionStrategy, Component, HostBinding, HostListener, OnInit, computed, inject, signal } from '@angular/core';
import { DialogRef, L_MODAL_DATA } from '@shared/components/dialog';
import { IconComponent } from '@shared/components/icon/icon.component';
import { PLATFORM_NAME } from '@shared/constants/platform.constants';
import { MarkdownModule, provideMarkdown } from 'ngx-markdown';
import { LEGAL_SLUGS, LegalDocumentDialogData, isLegalSlug } from '../legal.models';
import { LegalService } from '../services/legal/legal.service';

const LEGAL_HREF_PATTERN = /\/legal\/(terms|privacy|cookies)\/?$/;

const LEGAL_DOCUMENT_MODAL_CLASSES = 'block w-[min(100vw-2rem,42rem)] max-h-[90vh] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl font-poppins';

@Component({
  selector: 'l-legal-document-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MarkdownModule, IconComponent],
  providers: [provideMarkdown()],
  templateUrl: './legal-document-dialog.component.html',
  styles: `
    :host ::ng-deep .legal-markdown {
      color: #374151;
      font-size: 0.875rem;
      line-height: 1.7;
    }

    :host ::ng-deep .legal-markdown h1:first-child {
      display: none;
    }

    :host ::ng-deep .legal-markdown h1,
    :host ::ng-deep .legal-markdown h2,
    :host ::ng-deep .legal-markdown h3,
    :host ::ng-deep .legal-markdown h4 {
      color: #111827;
      font-weight: 600;
      line-height: 1.35;
    }

    :host ::ng-deep .legal-markdown h2 {
      margin: 1.75rem 0 0.75rem;
      padding-bottom: 0.5rem;
      font-size: 1rem;
      border-bottom: 1px solid #e5e7eb;
    }

    :host ::ng-deep .legal-markdown h3 {
      margin: 1.25rem 0 0.5rem;
      font-size: 0.9375rem;
    }

    :host ::ng-deep .legal-markdown p {
      margin: 0.75rem 0;
    }

    :host ::ng-deep .legal-markdown strong {
      color: #111827;
      font-weight: 600;
    }

    :host ::ng-deep .legal-markdown a {
      color: #0b3741;
      font-weight: 500;
      text-decoration: underline;
      text-underline-offset: 2px;
    }

    :host ::ng-deep .legal-markdown a:hover {
      color: #cc3366;
    }

    :host ::ng-deep .legal-markdown ul,
    :host ::ng-deep .legal-markdown ol {
      margin: 0.75rem 0;
      padding-left: 1.375rem;
    }

    :host ::ng-deep .legal-markdown ul {
      list-style: disc;
    }

    :host ::ng-deep .legal-markdown ol {
      list-style: decimal;
    }

    :host ::ng-deep .legal-markdown li {
      margin: 0.35rem 0;
    }

    :host ::ng-deep .legal-markdown hr {
      margin: 1.5rem 0;
      border: 0;
      border-top: 1px solid #e5e7eb;
    }

    :host ::ng-deep .legal-markdown blockquote {
      margin: 1rem 0;
      padding: 0.75rem 1rem;
      border-left: 3px solid #0b3741;
      border-radius: 0 0.5rem 0.5rem 0;
      background: #f9fafb;
      color: #4b5563;
    }

    :host ::ng-deep .legal-markdown table {
      width: 100%;
      margin: 1rem 0;
      border-collapse: collapse;
      font-size: 0.8125rem;
    }

    :host ::ng-deep .legal-markdown th,
    :host ::ng-deep .legal-markdown td {
      border: 1px solid #e5e7eb;
      padding: 0.5rem 0.75rem;
      text-align: left;
    }

    :host ::ng-deep .legal-markdown th {
      background: #f3f4f6;
      color: #111827;
      font-weight: 600;
    }
  `
})
export class LegalDocumentDialogComponent implements OnInit {
  @HostBinding('class')
  protected readonly hostClass = LEGAL_DOCUMENT_MODAL_CLASSES;

  private readonly data = inject(L_MODAL_DATA) as LegalDocumentDialogData;

  private readonly dialogRef = inject(DialogRef);

  private readonly legalService = inject(LegalService);

  protected readonly platformName = PLATFORM_NAME;

  protected readonly loading = signal(true);

  protected readonly hasError = signal(false);

  protected readonly markdownContent = signal('');

  protected readonly pageTitle = computed(() => LEGAL_SLUGS[this.data.slug].title);

  public ngOnInit(): void {
    void this.loadDocument();
  }

  protected close(): void {
    this.dialogRef.close();
  }

  @HostListener('click', ['$event'])
  protected onLegalContentClick(event: MouseEvent): void {
    const anchor = (event.target as HTMLElement).closest('a');

    if (!anchor) {
      return;
    }

    const href = anchor.getAttribute('href') ?? '';
    const match = LEGAL_HREF_PATTERN.exec(href);

    if (!match) {
      return;
    }

    const slug = match[1];

    if (!isLegalSlug(slug) || slug === this.data.slug) {
      return;
    }

    event.preventDefault();
    this.legalService.openDocument(slug);
  }

  private async loadDocument(): Promise<void> {
    const content = await this.legalService.loadMarkdown(LEGAL_SLUGS[this.data.slug].markdownPath);

    if (content.trim()) {
      this.markdownContent.set(content);
    } else {
      this.hasError.set(true);
    }

    this.loading.set(false);
  }
}
